import { formatAsCurrency } from "Libs/utils";

import type { LineItemComponent, Order, OrderLineItem } from "@packages/client";

export type OrderLineItemsNormalized = Record<
  string,
  {
    title: string | null;
    line_items: OrderLineItem[];
    components: undefined;
  }
>;

// Nested loops here because we need to add up the totals for *each project*
// based on each component *within the project*.
export const getTotalWithTax = (
  components: Record<string, LineItemComponent>
) =>
  Object.values(components).reduce((currentTotal: number, item) => {
    return currentTotal + item.amount;
  }, 0);

export const reducedTotalList = (
  lineItems: OrderLineItem[] | undefined,
  defaultTotal: Record<string, LineItemComponent>
) =>
  lineItems?.reduce((totalItems, item) => {
    Object.keys(item.components).forEach(component => {
      if (totalItems[component]) {
        totalItems[component].amount =
          item.components[component].amount + totalItems[component].amount;
      } else {
        totalItems[component] = {
          amount: item.components[component].amount,
          display_title: item.components[component].display_title
        };
      }
    });

    totalItems.totalPrice.amount =
      totalItems?.totalPrice?.amount + getTotalWithTax(item.components);
    return totalItems;
  }, defaultTotal);

export const formattedOrderProjectTotal = ({
  totals,
  lineItems,
  currency
}: {
  totals?: Record<string, LineItemComponent>;
  lineItems?: OrderLineItem[];
  currency?: string;
}) => {
  const defaultTotal: Record<string, LineItemComponent> = {
    totalPrice: {
      amount: 0,
      display_title: "Total"
    }
  };

  const totalList = totals || reducedTotalList(lineItems, defaultTotal);
  const finalTotal = totals && getTotalWithTax(totals);
  const formattedTotal = formatAsCurrency(
    typeof finalTotal === "undefined" || isNaN(finalTotal)
      ? totalList?.totalPrice?.amount || 0
      : finalTotal,
    currency
  );
  return { totalList, formattedTotal };
};

export const lineItemsByOrder = (order: Order | undefined) => {
  let normalizedLineItems: OrderLineItemsNormalized | undefined;
  if (order?.line_items) {
    normalizedLineItems = order.line_items
      .filter(
        item =>
          item.type !== "organization_feature" &&
          item.product !== "Onboarding - Training"
      )
      .reduce<OrderLineItemsNormalized>((items, item) => {
        if (!items[item.subscription_id]) {
          items[item.subscription_id] = {
            title: item.project,
            line_items: [],
            components: undefined
          };
        }
        items[item.subscription_id].line_items.push(item);
        return items;
      }, {});
  }

  return normalizedLineItems;
};

export const countValue = ({
  lineItems,
  key,
  subscriptionId
}: {
  lineItems?: OrderLineItemsNormalized;
  key: string;
  subscriptionId: string;
}) => {
  if (key === "tax") {
    // eslint-disable-next-line no-console
    console.error('Key must not equal "tax". Use `countTaxValue()` instead.');
    return 0;
  }

  return (
    lineItems?.[subscriptionId]?.line_items
      .filter(item => !item.exclude_from_invoice)
      .map(item => item?.components?.[key]?.amount)
      .filter(i => i)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0) ?? 0
  );
};

export const countTaxValue = ({
  lineItems,
  subscriptionId
}: {
  lineItems?: OrderLineItemsNormalized;
  subscriptionId: string;
}) => {
  const items = lineItems?.[subscriptionId]?.line_items;

  if (!items) return;

  const components = items
    .filter(item => !item.exclude_from_invoice)
    .map(item => item?.components);

  const taxComponentKeys = components?.flatMap(component => {
    return Object.keys(component).filter(
      i => i.includes("tax") || i.includes("vat")
    );
  });

  // In case there's more than one tax
  const uniqueKeys = taxComponentKeys?.filter(
    (value, index, array) => array.indexOf(value) === index
  );

  return uniqueKeys?.map(key => ({
    value:
      Math.round(
        items
          .filter(item => !item.exclude_from_invoice)
          .map(item => item?.components?.[key]?.amount)
          .filter(i => i)
          .reduce(
            (accumulator, currentValue) => accumulator + currentValue,
            0
          ) * 100
      ) / 100,
    taxKey: key
  }));
};
